import { useState } from "react";
import { NotePencil, PaperPlaneTilt, FilePdf, FileCode, CircleNotch, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { blobError } from "../../lib/download";
import { downloadCcePdf, downloadCceXml } from "./nfeActions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

export const CceDialog = ({ note, onClose, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(null);
  const [cces, setCces] = useState(note.cartas_correcao || []);

  const emit = async () => {
    if (text.trim().length < 15) {
      toast.error("O texto da correção deve ter pelo menos 15 caracteres");
      return;
    }
    if (!window.confirm("Emitir a Carta de Correção Eletrônica para esta NF-e?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/nfe/notes/${note.id}/cce`, { correcao: text.trim() });
      setCces(data.cartas_correcao || []);
      setText("");
      toast.success("Carta de Correção emitida com sucesso");
      onDone?.(data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const pdf = async (seq) => {
    setDl(`pdf-${seq}`);
    try { await downloadCcePdf(note, seq); toast.success(`PDF da CC-e nº ${seq} baixado`); }
    catch (err) { toast.error(await blobError(err, "Erro ao baixar o PDF da CC-e"), { duration: 12000 }); }
    setDl(null);
  };

  const xml = async (seq) => {
    setDl(`xml-${seq}`);
    try { await downloadCceXml(note, seq); }
    catch (err) { toast.error(await blobError(err, "Erro ao baixar o XML da CC-e"), { duration: 12000 }); }
    setDl(null);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="cce-dialog" className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <NotePencil size={20} className="text-sky-400" />
            Carta de Correção — NF-e {note.numero ? `nº ${note.numero}` : note.ref}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Pedido #{note.order_code} · {note.client_name}
          </DialogDescription>
        </DialogHeader>

        <div data-testid="cce-legal-warning" className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded px-3.5 py-3 leading-relaxed">
          <p className="font-semibold flex items-center gap-1.5 mb-1"><Warning size={14} weight="fill" /> Limites legais da CC-e</p>
          A Carta de Correção <b>NÃO pode</b> alterar: valores (preço, quantidade, impostos, totais),
          data de emissão ou de saída, nem o destinatário/remetente. Ela serve para corrigir dados como
          endereço, descrição de produtos, transportadora, volumes e informações complementares.
          Para erros de valor, cancele a nota (prazo legal) e emita outra.
        </div>

        {cces.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Cartas já emitidas ({cces.length})</p>
            {cces.map((c) => (
              <div key={c.seq} data-testid={`cce-item-${c.seq}`} className="border border-zinc-800 rounded-lg px-3.5 py-2.5 text-sm">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-sky-300 font-medium">CC-e nº {c.seq}</span>
                  <span className="text-xs text-zinc-500">{new Date(c.criado_em).toLocaleString("pt-BR")} · {c.criado_por}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button data-testid={`cce-pdf-${c.seq}`} onClick={() => pdf(c.seq)} disabled={dl !== null}
                      title="Baixar o DACCE (PDF da Carta de Correção) gerado pela Focus NFe"
                      className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-medium px-2.5 py-1 rounded transition-colors">
                      {dl === `pdf-${c.seq}` ? <CircleNotch size={13} className="animate-spin" /> : <FilePdf size={13} />} Baixar PDF da CC-e
                    </button>
                    <button data-testid={`cce-xml-${c.seq}`} onClick={() => xml(c.seq)} disabled={dl !== null}
                      title="Baixar o XML do evento da CC-e"
                      className="flex items-center gap-1 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-50 text-zinc-300 text-xs font-medium px-2 py-1 rounded transition-colors">
                      {dl === `xml-${c.seq}` ? <CircleNotch size={13} className="animate-spin" /> : <FileCode size={13} />} XML
                    </button>
                  </div>
                </div>
                <p className="text-xs text-zinc-400 mt-1.5">{c.correcao}</p>
              </div>
            ))}
            <p className="text-[11px] text-zinc-600">Cada nova CC-e substitui a anterior perante a SEFAZ — inclua todas as correções vigentes no mesmo texto.</p>
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Texto da correção (15 a 1000 caracteres)</label>
          <textarea data-testid="cce-text-input" rows={4} maxLength={1000}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            placeholder="Ex: Onde se lê 'Rua das Flores, 10', leia-se 'Rua das Flores, 100'."
            value={text} onChange={(e) => setText(e.target.value)} />
          <p className="text-[11px] text-zinc-600 mt-1">{text.trim().length}/1000</p>
        </div>

        <div className="flex gap-2">
          <button data-testid="cce-submit-button" onClick={emit} disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded transition-colors">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />} Emitir Carta de Correção
          </button>
          <button data-testid="cce-close-button" onClick={onClose}
            className="border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-sm font-medium px-4 py-2.5 rounded transition-colors">
            Fechar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
